// Gráfico de barras do painel (DESIGN §5.22): SVG, barras tinta, hoje em marca, 3 linhas de grade,
// datas a cada 7 dias, tooltip no toque/hover e tabela sr-only com os dados.
import { useId, useState } from "react";
import { cn } from "../lib/cn";
import { formatarDataCurta, formatarDiaSemanaCurto } from "../lib/datas";
import { formatarNumero } from "../lib/numeros";

export interface PontoGrafico {
  data: string; // AAAA-MM-DD
  total: number;
}

export function GraficoBarras({
  rotulo,
  pontos,
  hoje,
  unidade = "entradas",
  altura = 120,
  className,
}: {
  rotulo: string;
  pontos: PontoGrafico[];
  hoje?: string;
  unidade?: string;
  altura?: number;
  className?: string;
}) {
  const [foco, setFoco] = useState<number | null>(null);
  const idTabela = useId();
  const max = Math.max(1, ...pontos.map((p) => p.total));
  const topo = Math.ceil(max / 3) * 3;
  const largura = 100 / Math.max(1, pontos.length);
  const ativo = foco !== null ? pontos[foco] : undefined;

  return (
    <figure className={cn("relative", className)} aria-describedby={idTabela}>
      <figcaption className="sr-only">{rotulo}</figcaption>
      <div className="flex gap-2">
        <div
          className="flex flex-col justify-between text-right text-rotulo text-texto-3 placar"
          style={{ height: altura }}
          aria-hidden
        >
          <span>{formatarNumero(topo)}</span>
          <span>{formatarNumero(Math.round(topo / 2))}</span>
          <span>0</span>
        </div>
        <svg
          viewBox={`0 0 100 ${altura}`}
          preserveAspectRatio="none"
          className="flex-1 overflow-visible"
          style={{ height: altura }}
          aria-hidden
          onMouseLeave={() => setFoco(null)}
        >
          {[0, 0.5, 1].map((f) => (
            <line
              key={f}
              x1={0}
              x2={100}
              y1={altura * f}
              y2={altura * f}
              className="stroke-fio"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {pontos.map((p, i) => {
            const h = (p.total / topo) * altura;
            return (
              <rect
                key={p.data}
                x={i * largura + largura * 0.1}
                y={altura - h}
                width={largura * 0.8}
                height={Math.max(h, 0)}
                rx={0.6}
                className={cn(p.data === hoje ? "fill-marca" : "fill-tinta", foco === i && "opacity-80")}
                onMouseEnter={() => setFoco(i)}
                onClick={() => setFoco(i)}
              />
            );
          })}
        </svg>
      </div>
      <div className="mt-1 flex justify-between pl-8 text-rotulo text-texto-3" aria-hidden>
        {pontos
          .filter((_, i) => i % 7 === 0 || i === pontos.length - 1)
          .map((p) => (
            <span key={p.data}>{formatarDataCurta(p.data)}</span>
          ))}
      </div>
      {ativo && (
        <div className="pointer-events-none absolute top-0 right-0 rounded-controle bg-tinta px-3 py-1.5 text-rotulo text-sobre-tinta shadow-toast">
          {formatarDiaSemanaCurto(ativo.data)}, {formatarDataCurta(ativo.data)} ·{" "}
          {formatarNumero(ativo.total)} {unidade}
        </div>
      )}
      <table id={idTabela} className="sr-only">
        <caption>{rotulo}</caption>
        <thead>
          <tr>
            <th scope="col">Dia</th>
            <th scope="col">Total</th>
          </tr>
        </thead>
        <tbody>
          {pontos.map((p) => (
            <tr key={p.data}>
              <td>{formatarDataCurta(p.data)}</td>
              <td>{p.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
